import React from "react";
import * as solutionConfig from "src/config/solutionConfigs.json";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Label } from "@amzn/stencil-react-components/text";
import { TextArea, Select } from '@amzn/stencil-react-components/form';
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { IconBin, IconSize } from '@amzn/stencil-react-components/icons';
import { useAppDispatch, useAppSelector } from "src/state/hooks";
import { updateEventSolutionDetailsObject } from "src/state/slice/EventModelSlice";
import {AttributeTypes, CommonConstants, CUSTOM_ATTRIBUTE_TYPES} from "src/constant/constants";
import { locales as supportedLocales } from "src/constant/locales";
import { isNullOrUndefined } from "src/util/commonUtil";
import { Spacer } from "@amzn/stencil-react-components/dist/submodules/layout";
import "src/components/commonStyles.scss";
import {ISolutionActionType, SolutionDetailAttribute} from "src/entity/entities";
import {FileUploader} from "src/components/authoringComponents/FileUploader";
import {TimestampInput} from "src/components/authoringComponents/TimestampInput";
import {CheckboxInput} from "src/components/authoringComponents/CheckboxInput";
import {LinkComponent} from "src/components/authoringComponents/LinkComponent";
import {IconWithTooltip} from "src/components/helperComponents/IconWithTooltip";
import {
  MessageBoardCmsIdInputComponent
} from "src/components/authoringComponents/MessageBoardAuthoring/MessageBoardCmsIdInputComponent";
import {
  MessageBoardContextAttributesComponent
} from "src/components/authoringComponents/MessageBoardAuthoring/MessageBoardContextAttributesComponent";
import {RichTextAuthoring} from "src/components/authoringComponents/RichTextAuthoring";

const STRINGS_DISPLAYED = {
  add_locale: "+ Add locale",
}

/**
 * Renders a form to let the user fill solution details for an event model, for a chosen solution type.
 */
export const SolutionDetailsRenderer = ({actionType}: ISolutionActionType) => {
  const dispatch = useAppDispatch();
  const eventModelState = useAppSelector(({eventModel}) => eventModel);
  const solutionDetailAttributes: SolutionDetailAttribute[] = solutionConfig[eventModelState?.eventSolution?.type]?.solutionDetailAttributes;

  const renderCustomAuthoringComponent = (attributeType: string, attributeName: string, attributeValue: any) => {
    switch (attributeType) {
      case AttributeTypes.FILE:
        return <FileUploader attributeName={attributeName} attributeValue={attributeValue} actionType={actionType} />
      case AttributeTypes.CHECKBOX:
        return <CheckboxInput attributeName={attributeName} attributeValue={attributeValue} />
      case AttributeTypes.LINK:
        return <LinkComponent attributeName={attributeName} attributeValue={attributeValue} />
      case AttributeTypes.MESSAGE_BOARD_CMS_ID_INPUT:
        return <MessageBoardCmsIdInputComponent attributeName={attributeName} attributeValue={attributeValue}/>
      case AttributeTypes.MESSAGE_BOARD_CONTEXT_ATTRIBUTE:
        return <MessageBoardContextAttributesComponent attributeName={attributeName} attributeValue={attributeValue} />
      case AttributeTypes.RICH_TEXT:
        return <RichTextAuthoring attributeName={attributeName} attributeValue={attributeValue} />
    }
  }

  return <Col gridGap={"12px"}>
    {(solutionDetailAttributes && eventModelState?.eventSolution?.solutionDetailObject)
    && solutionDetailAttributes.map((solutionDetailAttribute) => {

      // handle presentationDetail attributes
      if (solutionDetailAttribute?.attributeName?.startsWith(CommonConstants.PRESENTATION_DETAIL)) {
        return <RenderPresentationDetailAttribute 
          presentationDetailAttribute={solutionDetailAttribute}
        />
      }

      // handle all other solution detail attributes
      const attributeValue = eventModelState?.eventSolution?.solutionDetailObject[solutionDetailAttribute?.attributeName];
      return (<>
        <Row
          alignItems={"center"} 
          justifyContent={"space-between"}
          width={"fit-content"}
          key={`${solutionDetailAttribute.attributeName}-key`}
        >
          <Label 
            fontSize={"16px"} 
            htmlFor={`${solutionDetailAttribute.attributeName}-input`}
            className={"width_200_px"}
          >
            <>
              {`${solutionDetailAttribute.attributeName}: `}
              {solutionDetailAttribute.isRequired && <span className={"color_grey"}>*</span>}
              {
                  solutionDetailAttribute?.description && <IconWithTooltip
                      tooltipText={solutionDetailAttribute?.description}
                      color={"primary70"}
                  />
              }
            </>
          </Label>
          {
            CUSTOM_ATTRIBUTE_TYPES.includes(solutionDetailAttribute?.attributeType) ?
                renderCustomAuthoringComponent(solutionDetailAttribute?.attributeType, solutionDetailAttribute.attributeName, eventModelState?.eventSolution?.solutionDetailObject[solutionDetailAttribute?.attributeName]) :
          (solutionDetailAttribute.attributeType === "boolean")
            // Render a true/false selector for boolean attributes
            ? <Select
                id={`${solutionDetailAttribute.attributeName}-input`}
                width={"200px"}
                options={["true", "false"]}
                onChange={(value) => {
                  dispatch(updateEventSolutionDetailsObject({
                    [solutionDetailAttribute.attributeName]: (value === "true"),
                  }));
                }}
                value={typeof attributeValue === "boolean"
                  ? JSON.stringify(attributeValue)
                  : ""}
              />
            : (solutionDetailAttribute.options)
              // Render selector with options if options are present for the attribute
              ? <Select
                  id={`${solutionDetailAttribute.attributeName}-input`}
                  width={"200px"}
                  options={solutionDetailAttribute.options}
                  onChange={(value) => {
                    dispatch(updateEventSolutionDetailsObject({
                      [solutionDetailAttribute.attributeName]: value,
                    }));
                  }}
                  value={attributeValue}
                />
              // Render free form text area for all other attributes
              : <TextArea
                  id={`${solutionDetailAttribute.attributeName}-input`}
                  resize={"both"}
                  height={"45px"}
                  minHeight={"45px"}
                  width={"800px"}
                  onChange={(e) => {
                    let value: any = e?.target?.value;

                    // if attribute is of type number, then convert string input to number
                    if (solutionDetailAttribute.attributeType === "number") {
                      value = parseInt(value);
                    }

                    dispatch(updateEventSolutionDetailsObject({
                      [solutionDetailAttribute.attributeName]: value,
                    }));
                  }}
                  value={attributeValue}
                />
          }
        </Row>
        <HorizontalDevider />
      </>);
    })}
  </Col>
}

type RenderPresentationDetailAttributeProps = {
  presentationDetailAttribute: SolutionDetailAttribute,
}
const RenderPresentationDetailAttribute = ({presentationDetailAttribute}: RenderPresentationDetailAttributeProps) => {
  const dispatch = useAppDispatch();
  const eventModelState = useAppSelector(({eventModel}) => eventModel);

  const presentationDetailObject = eventModelState.eventSolution.solutionDetailObject[CommonConstants.PRESENTATION_DETAIL];
  const currentLocales = Object.keys(presentationDetailObject);

  const presentationDetailAttributeName = presentationDetailAttribute?.attributeName?.split(".")[1];

  return presentationDetailAttributeName && <>
    <Row
      alignItems={"center"} 
      justifyContent={"space-between"}
      width={"fit-content"}
      key={`${presentationDetailAttributeName}-key`}
    >
      <Label 
        fontSize={"16px"}
        htmlFor={`${presentationDetailAttributeName}-col`}
        className={"width_200_px"}
      >
        <>
          {`${presentationDetailAttributeName}: `}
          {presentationDetailAttribute.isRequired && <span className={"color_grey"}>*</span>}
        </>
      </Label>

      <Col 
        id={`${presentationDetailAttributeName}-col`}
        gridGap={"8px"}
      >
        {/* Render elements for each locale present for the presentationDetail attribute */}
        {currentLocales && currentLocales.map((locale) => {
          return (presentationDetailObject[locale] && !isNullOrUndefined(presentationDetailObject[locale][presentationDetailAttributeName]))
            && (
              <Row
                alignItems={"center"} 
                justifyContent={"space-between"}
                width={"fit-content"}
                key={`${presentationDetailAttributeName}-${locale}-row`}
              >
                <Label 
                  fontSize={"16px"}
                  className={"width_150_px"}
                >
                  {locale}
                </Label>
                
                <TextArea
                  resize={"both"}
                  height={"45px"}
                  minHeight={"45px"}
                  width={"650px"}
                  value={presentationDetailObject[locale][presentationDetailAttributeName]}
                  onChange={(e) => {
                    dispatch(updateEventSolutionDetailsObject({
                      [CommonConstants.PRESENTATION_DETAIL]: {
                        ...presentationDetailObject,
                        [locale]: {
                          ...presentationDetailObject[locale],
                          [presentationDetailAttributeName]: e?.target?.value,
                        }
                      }
                    }));
                  }}
                />

                <Spacer width={"8px"}/>
                {/* Button for deleting entry for a locale */}
                <Button
                  variant={ButtonVariant.Tertiary}
                  isDestructive={true}
                  icon={<IconBin size={IconSize.Small}/>}
                  onClick={() => {
                    dispatch(updateEventSolutionDetailsObject({
                      [CommonConstants.PRESENTATION_DETAIL]: {
                        ...presentationDetailObject,
                        [locale]: {
                          ...presentationDetailObject[locale],
                          [presentationDetailAttributeName]: undefined,
                        }
                      }
                    }));
                  }}
                />
              </Row>
            );
        })}
        
        {/* Render "Add locale" option to let users add values for more locales */}
        <Row
          alignItems={"center"} 
          justifyContent={"space-between"}
          width={"fit-content"}
        >
          <Label 
            fontSize={"16px"}
            className={"width_150_px"}
          >
            {STRINGS_DISPLAYED.add_locale}
          </Label>

          <Select
            options={supportedLocales}
            width={"200px"}
            onChange={(value) => {
              dispatch(updateEventSolutionDetailsObject({
                [CommonConstants.PRESENTATION_DETAIL]: {
                  ...presentationDetailObject,
                  [value]: {
                    ...presentationDetailObject[value],
                    [presentationDetailAttributeName]: "",
                  }
                }
              }));
            }}
          />
        </Row>
      </Col>
    </Row>
    <HorizontalDevider />
  </>
}

const HorizontalDevider = () => <View width={"1000px"} height={"1px"} backgroundColor={"#c7c7c7"}/>;
